/* Cassaforte · apertura di un wallet salvato.
 *
 * Un solo meccanismo di apertura, ed è il secondo difetto che il documento di
 * redesign imputa alla schermata precedente: lì c'erano DUE pulsanti «Decrypt»
 * che facevano cose diverse (uno sul modulo di creazione, uno sulla riga della
 * tabella) e nessuno dei due diceva su quale wallet stesse lavorando.
 *
 * Tre cose che qui non succedono:
 *  - i valori non arrivano al browser finché non si apre QUESTA finestra: la
 *    lista chiede solo `id,name,createdAt` (vedi `dati.ts`);
 *  - decifrati, restano mascherati: mostrarli è un gesto in più, copiarli non
 *    richiede mostrarli;
 *  - la finestra si richiude da sola dopo due minuti e svuota tutto, perché
 *    una chiave privata a schermo su una scrivania condivisa è il modo più
 *    banale di perdere un wallet.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Avvertenza, Distintivo, Pulsante } from '../../componenti';
import { AzioneDistruttiva } from '../../componenti/AzioneDistruttiva';
import { Campo } from '../../componenti/Modulo';
import { Modale } from '../../componenti/Sovrapposizioni';
import { Rotella } from '../../componenti/Stati';
import { personalWallets } from '../../api/risorse';
import { ErroreApi } from '../../api/client';
import { usaRichiesta } from '../../api/usaRichiesta';
import { dataOra } from '../../lib/formato';
import { decifra, ErroreCifratura, formatoDi } from './cifratura';
import { copiaNegliAppunti, mascherato, type VoceCassaforte } from './dati';

/* Due minuti. Abbastanza per copiare una chiave in un altro programma, poco
   abbastanza da non lasciarla a schermo mentre si va a prendere un caffè. */
const CHIUSURA_AUTOMATICA_MS = 120_000;

interface Aperto {
  indirizzo: string;
  chiave: string;
  mnemonica: string | null;
}

interface Props {
  voce: VoceCassaforte | null;
  onChiudi: () => void;
  onEliminato: (nome: string) => void;
  onErrore: (messaggio: string) => void;
}

export function AperturaWallet({ voce, onChiudi, onEliminato, onErrore }: Props) {
  const id = voce?.id ?? null;

  const [password, setPassword] = useState('');
  const [aperto, setAperto] = useState<Aperto | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [inEliminazione, setInEliminazione] = useState(false);
  const [copiato, setCopiato] = useState<string | null>(null);

  /* Il testo cifrato si scarica solo qui, e solo per il wallet che si sta
     aprendo: `attiva` tiene ferma la richiesta finché non c'è un id. */
  const riga = usaRichiesta(() => personalWallets.uno(id as number), [id], { attiva: id != null });

  const svuota = useCallback(() => {
    setPassword('');
    setAperto(null);
    setErrore(null);
    setInCorso(false);
    setInEliminazione(false);
    setCopiato(null);
  }, []);

  /* Alla chiusura, non all'apertura: vale la stessa ragione del modulo di
     creazione, cioè non tenere un segreto in memoria fino al prossimo giro. */
  useEffect(() => {
    if (voce == null) svuota();
  }, [voce, svuota]);

  /* `onChiudi` arriva come lambda inline dal genitore, quindi cambia identità a
     ogni render. Tenerla in un ref, invece che fra le dipendenze, è ciò che
     rende i due minuti davvero due minuti: altrimenti ogni ridisegno (premere
     «Mostra», copiare un valore) rimonterebbe l'effetto e farebbe ripartire il
     conto da capo, con la chiave a schermo a tempo indeterminato. */
  const chiudiRef = useRef(onChiudi);
  chiudiRef.current = onChiudi;

  /* La chiusura automatica parte da quando i valori compaiono, non da quando
     si apre la finestra: chi sta ancora digitando la password non viene
     buttato fuori a metà. */
  useEffect(() => {
    if (aperto == null) return;
    const t = setTimeout(() => {
      svuota();
      chiudiRef.current();
    }, CHIUSURA_AUTOMATICA_MS);
    return () => clearTimeout(t);
  }, [aperto, svuota]);

  const dati = riga.dati;
  const formato = dati?.privateKey ? formatoDi(dati.privateKey) : null;

  async function apri() {
    if (!dati) return;
    setErrore(null);
    setInCorso(true);
    try {
      const indirizzo = await decifra(dati.address, password);
      const chiave = await decifra(dati.privateKey ?? '', password);
      /* La frase mnemonica è facoltativa: un wallet importato da una chiave
         non ne ha una, e non è un errore da mostrare come tale. */
      const mnemonica = dati.mnemonic ? await decifra(dati.mnemonic, password) : null;
      setAperto({ indirizzo, chiave, mnemonica });
      setPassword('');
    } catch (e) {
      setErrore(e instanceof ErroreCifratura ? e.message : 'Apertura non riuscita.');
    } finally {
      setInCorso(false);
    }
  }

  async function elimina() {
    if (id == null || voce == null) return;
    setInEliminazione(true);
    try {
      await personalWallets.elimina(id);
      const nome = voce.nome;
      svuota();
      onEliminato(nome);
    } catch (e) {
      onErrore(e instanceof ErroreApi ? e.message : 'Eliminazione non riuscita.');
      setInEliminazione(false);
    }
  }

  async function copia(etichetta: string, valore: string) {
    const fatto = await copiaNegliAppunti(valore);
    setCopiato(fatto ? etichetta : null);
    if (!fatto) setErrore('Il browser non ha concesso gli appunti: mostra il valore e copialo a mano.');
  }

  return (
    <Modale
      aperta={voce != null}
      onChiudi={onChiudi}
      titolo={voce?.nome ?? 'Wallet'}
      sottotitolo={voce?.creatoIl ? `salvato il ${dataOra(voce.creatoIl)}` : undefined}
      larga
      pieda={
        <AzioneDistruttiva
          etichetta="Elimina dalla cassaforte"
          etichettaArmata="Confermi? Elimina davvero"
          nota="Questo wallet sparisce dal database. Se non hai la chiave altrove, il wallet è perso: non c'è un cestino."
          onConferma={elimina}
          disabilitata={inEliminazione}
        />
      }
    >
      <div className="sb-colonna" style={{ gap: 'var(--sb-s-5)' }}>
        {errore && <Avvertenza livello="grave">{errore}</Avvertenza>}

        {riga.stato === 'caricamento' && (
          <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
            <Rotella dim={18} />
            <span className="sb-nota">carico il wallet cifrato…</span>
          </div>
        )}

        {riga.stato === 'errore' && (
          <Avvertenza livello="grave">
            {riga.errore?.message ?? 'Il wallet non è stato letto.'}{' '}
            <Pulsante variante="nudo" onClick={riga.ricarica}>
              Riprova
            </Pulsante>
          </Avvertenza>
        )}

        {dati && aperto == null && (
          <>
            {formato === 'legacy' && (
              <Avvertenza livello="nota">
                Wallet salvato dall'interfaccia precedente: si apre con la password di allora. La
                cifratura di allora era più debole di quella di oggi, ma il dato non cambia da
                solo. Per portarlo al formato nuovo: aprilo, copia i valori, salvalo come wallet
                nuovo e poi elimina questo.
              </Avvertenza>
            )}
            {formato == null && (
              <Avvertenza livello="grave">
                Questa riga non è stata scritta dalla cassaforte: non ha né il preambolo di oggi né
                quello dell'interfaccia precedente. Non c'è niente da decifrare.
              </Avvertenza>
            )}

            <Campo
              etichetta="Password"
              testuale
              type="password"
              suggerimento="quella scelta quando hai salvato questo wallet"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && password !== '' && !inCorso) void apri();
              }}
              autoComplete="off"
              spellCheck={false}
              disabled={formato == null}
            />
            <Pulsante
              variante="primario"
              largo
              disabled={password === '' || inCorso || formato == null}
              onClick={apri}
            >
              {inCorso ? 'Apro…' : 'Apri'}
            </Pulsante>
          </>
        )}

        {aperto && (
          <>
            <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
              <Distintivo tono="cautela">aperto</Distintivo>
              <span className="sb-nota">si richiude da sola fra due minuti</span>
            </div>

            <RigaSegreto
              etichetta="Indirizzo pubblico"
              valore={aperto.indirizzo}
              copiato={copiato === 'Indirizzo pubblico'}
              onCopia={() => copia('Indirizzo pubblico', aperto.indirizzo)}
            />
            <RigaSegreto
              etichetta="Chiave privata"
              valore={aperto.chiave}
              copiato={copiato === 'Chiave privata'}
              onCopia={() => copia('Chiave privata', aperto.chiave)}
            />
            {aperto.mnemonica ? (
              <RigaSegreto
                etichetta="Frase mnemonica"
                valore={aperto.mnemonica}
                copiato={copiato === 'Frase mnemonica'}
                onCopia={() => copia('Frase mnemonica', aperto.mnemonica as string)}
              />
            ) : (
              <div className="sb-cs-segreto">
                <span className="sb-etichetta">Frase mnemonica</span>
                <span className="sb-nota">nessuna: questo wallet si recupera solo con la chiave privata</span>
              </div>
            )}

            <Pulsante variante="tenue" largo onClick={svuota}>
              Richiudi adesso
            </Pulsante>
          </>
        )}
      </div>
    </Modale>
  );
}

/* ==========================================================================
 * Una riga di valore aperto
 * ======================================================================== */

function RigaSegreto({
  etichetta,
  valore,
  copiato,
  onCopia,
}: {
  etichetta: string;
  valore: string;
  copiato: boolean;
  onCopia: () => void;
}) {
  /* Mascherato di default e per ogni riga separatamente: mostrare la chiave
     privata non deve essere il prezzo da pagare per leggere l'indirizzo. */
  const [mostra, setMostra] = useState(false);

  return (
    <div className="sb-cs-segreto">
      <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
        <span className="sb-etichetta" style={{ flex: 1 }}>
          {etichetta}
        </span>
        <Pulsante variante="nudo" onClick={() => setMostra((v) => !v)}>
          {mostra ? 'Nascondi' : 'Mostra'}
        </Pulsante>
        <Pulsante variante="nudo" onClick={onCopia}>
          {copiato ? 'Copiato' : 'Copia'}
        </Pulsante>
      </div>
      <div className="sb-cs-mono sb-cs-segreto__valore">{mostra ? valore : mascherato(valore)}</div>
    </div>
  );
}
